import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react'

const testimonials = [
  {
    id: 1,
    name: "Rafael Silva",
    role: "Estamparia Digital",
    content: "O Spot White é incrível. Com apenas 1 click automático, ele gera a camada de spot branca perfeita, com ou sem contração. Economizo muito tempo e tinta na minha impressora DTF.",
    rating: 5,
    avatar: "RS"
  },
  {
    id: 2,
    name: "Estúdio Criativo BR",
    role: "Produção DTF",
    content: "O DTF Factory entrega artes prontas para impressão, totalmente sem fundo e em 300dpi. A qualidade é absurda e já sai pronto para o RIP.",
    rating: 5,
    avatar: "EC"
  },
  {
    id: 3,
    name: "Ana Clara",
    role: "Designer Gráfico",
    content: "O Melhorador Cloud salvou várias estampas que clientes mandaram em baixa resolução. A nitidez fica profissional e recupera detalhes perdidos.",
    rating: 5,
    avatar: "AC"
  },
  {
    id: 4,
    name: "Lucas Mendes",
    role: "Sublimação Total",
    content: "Uso o Melhorador Desktop todo dia. É leve, rápido e roda direto no meu PC sem travar nada. Essencial para o dia a dia da minha produção.",
    rating: 5,
    avatar: "LM"
  }
]

export default function TestimonialCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [direction, setDirection] = useState(0)

  // Auto-play
  useEffect(() => {
    const timer = setInterval(() => {
      nextSlide()
    }, 5000)
    return () => clearInterval(timer)
  }, [currentIndex])

  const nextSlide = () => {
    setDirection(1)
    setCurrentIndex((prev) => (prev + 1) % testimonials.length)
  }

  const prevSlide = () => {
    setDirection(-1)
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length)
  }

  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0,
      scale: 0.95
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
      scale: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 50 : -50,
      opacity: 0,
      scale: 0.95
    })
  }

  return (
    <div className="w-full max-w-4xl mx-auto mt-20 px-4">
      <div className="text-center mb-10">
        <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">O que dizem nossos usuários</h3>
        <div className="h-1 w-20 bg-gradient-to-r from-cyan-500 to-purple-500 mx-auto rounded-full" />
      </div>

      <div className="relative h-[250px] md:h-[200px] flex items-center justify-center">
        {/* Navigation Buttons */}
        <button 
          onClick={prevSlide}
          className="absolute left-0 z-10 p-2 rounded-full bg-white/5 hover:bg-white/10 text-white transition-colors backdrop-blur-sm border border-white/10"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        
        <button 
          onClick={nextSlide}
          className="absolute right-0 z-10 p-2 rounded-full bg-white/5 hover:bg-white/10 text-white transition-colors backdrop-blur-sm border border-white/10"
        >
          <ChevronRight className="w-6 h-6" />
        </button>

        <div className="w-full max-w-2xl overflow-hidden relative h-full flex items-center justify-center">
          <AnimatePresence initial={false} custom={direction} mode="wait">
            <motion.div
              key={currentIndex}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 }
              }}
              className="absolute w-full px-8 md:px-12"
            >
              <div className="bg-black/40 backdrop-blur-md border border-white/10 rounded-2xl p-8 relative">
                <Quote className="absolute top-4 left-4 w-8 h-8 text-cyan-500/20" />
                
                <div className="flex flex-col items-center text-center">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        className={`w-4 h-4 ${i < testimonials[currentIndex].rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'}`} 
                      />
                    ))}
                  </div>

                  <p className="text-gray-300 text-lg italic mb-6 leading-relaxed">
                    "{testimonials[currentIndex].content}"
                  </p>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-600 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                      {testimonials[currentIndex].avatar}
                    </div>
                    <div className="text-left">
                      <h4 className="text-white font-bold text-sm">{testimonials[currentIndex].name}</h4>
                      <p className="text-cyan-400/70 text-xs font-mono">{testimonials[currentIndex].role}</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Dots Indicator */}
      <div className="flex justify-center gap-2 mt-4">
        {testimonials.map((_, index) => (
          <button
            key={index}
            onClick={() => {
              setDirection(index > currentIndex ? 1 : -1)
              setCurrentIndex(index)
            }}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentIndex ? 'bg-cyan-400 w-6' : 'bg-gray-700 hover:bg-gray-600'
            }`}
          />
        ))}
      </div>
    </div>
  )
}
